const COMMA_SAFE_ARRAY_AUGMENTATION = "!@#$%^&*(comma-safe)*&^%$#@!"; // keep in sync with hybrid-web-export

/**
 * I accept an object containing parameters to send w/ an XHR request and
 * convert them to a query string. Of particular note, array parameters are
 * serialized as multiple instances of "the same" parameter, with no suffix.
 * This is in contrast to jQuery, which adds "[]" to array params to support
 * PHP which will ignore repeated params of the same name (keeping on the last)
 * unless the name itself indicates that the param is supposed to be an array
 * (via the suffix). Yay for dynamic languages? This doesn't do that.
 *
 * @param params an object w/ param names for keys and param values as values.
 * @return {string} a query string version of the params.
 */
export default function serializeParams(params) {
    if (!params) return "";
    const result = [];
    Object.keys(params).forEach(name => {
        const value = params[name];
        if (value instanceof Array) {
            value.forEach(it =>
                result.push([ name, it ]));
        } else {
            result.push([ name, value ]);
        }
    });
    return result.map(([ n, v ]) =>
        encodeURIComponent(n) + "=" + encodeURIComponent(v))
        .join("&");
}

/**
 * I accept an object containing parameters to send w/ an XHR request, along
 * with varargs of keys which are array that need to be made comma-safe. For
 * example, the values <tt>"jones, john"</tt> and <tt>["jones, john"]</tt> will
 * serialize to the same query string: <tt>?key=jones%2C%20john</tt>. If the
 * server accepts a list, it will treat the in-value comma as a delimiter, and
 * assume <tt>["jones", " john"]</tt> was intended. By passing an extra garbage
 * parameter of the same name (e.g., <tt>?key=jones%2C%20john&key=garbage</tt>),
 * the server will assume that each same-named parameter is a single value, that
 * is <tt>>["jones, john", "garbage"]</tt> was intended.
 *
 * <p>If the array contains the empty string as its sole value, the entire param
 * key will be deleted instead.
 *
 * <p>This is pretty kludge, but such is life.
 *
 * @param params an object w/ param names for keys and param values as values.
 * @param paramKeys an array of param names that should be made comma safe.
 * @return {object} an updated params object with comma-safe array parameters.
 */
export function toCommaSafeArray(params, ...paramKeys) {
    params = { ...params };
    paramKeys.forEach(k => {
        if (params[k] && params[k].length === 1) {
            if (params[k][0] === "") {
                delete params[k];
            } else {
                params[k] = params[k].concat(COMMA_SAFE_ARRAY_AUGMENTATION);
            }
        }
    });
    return params;
}

/**
 * I reverse what {@link toCommaSafeArray} does.
 * @param params
 * @param paramKeys
 * @return {*}
 */
export function fromCommaSafeArray(params, ...paramKeys) {
    params = { ...params };
    paramKeys.forEach(k => {
        if (!params[k] || params[k].length === 0) {
            params[k] = [ "" ]; // all
        } else {
            params[k] = params[k].filter(it =>
                it !== COMMA_SAFE_ARRAY_AUGMENTATION);
        }
    });
    return params;
}

export function stringifyJson(params, ...paramKeys) {
    params = { ...params };
    paramKeys.forEach(k => {
        if (params[k] && typeof params[k] !== "string") {
            params[k] = JSON.stringify(params[k]);
        }
    });
    return params;
}

export function unstringifyJson(params, ...paramKeys) {
    params = { ...params };
    paramKeys.forEach(k => {
        if (typeof params[k] === "string") {
            try {
                params[k] = JSON.parse(params[k]);
            } catch (e) {
                console.warn(`failed to deserialize ${k}`, e, params[k]);
                params[k] = {}; // bummer
            }
        }
    });
    return params;
}



// WEBPACK FOOTER //
// ./app/lib/hex/views/components/serializeParams.js